import React from 'react';
import DammsAnalyticCard from './DammsAnalyticCard';
import { DamsColors } from '../styles/DamsColors';

export interface DashboardMetricCardProps {
  title: string;
  value: number;
  subtitle?: string;
  trend?: {
    value: number;
    isPositive: boolean;
    period: string;
  };
  icon?: React.ReactNode;
  color?: string;
  loading?: boolean;
  onClick?: () => void;
  breakdown?: {
    active: number;
    inactive: number;
  };
}

export const DashboardMetricCard: React.FC<DashboardMetricCardProps> = ({
  title,
  value,
  subtitle,
  trend,
  icon,
  color = DamsColors.Button.Primary,
  loading = false,
  onClick,
  breakdown
}) => {
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const getTrendIcon = () => {
    if (!trend) return null;
    return trend.isPositive ? '↗️' : '↘️';
  };

  const getTrendColor = () => {
    if (!trend) return '#6B7280'; // gray-500
    return trend.isPositive ? DamsColors.Badge.Green.text : DamsColors.Badge.Red.text;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-8 w-8 bg-gray-200 rounded"></div>
        </div>
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
      </div>
    );
  }

  return (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 transition-all duration-200 hover:shadow-md ${
        onClick ? 'cursor-pointer hover:border-blue-300' : ''
      }`}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        {icon && (
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${color}20`, color }}
          >
            {icon}
          </div>
        )}
      </div>

      {/* Main Value */}
      <div className="mb-3">
        <div className="text-3xl font-bold text-gray-900 mb-1">
          {formatNumber(value)}
        </div>
        {subtitle && (
          <div className="text-sm text-gray-500">{subtitle}</div>
        )}
      </div>

      {/* Breakdown */}
      {breakdown && (
        <div className="mb-3">
          <div className="flex justify-between text-xs text-gray-600">
            <span>Active: <strong className="text-green-600">{formatNumber(breakdown.active)}</strong></span>
            <span>Inactive: <strong className="text-red-600">{formatNumber(breakdown.inactive)}</strong></span>
          </div>
          <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-500 transition-all duration-300"
              style={{ 
                width: `${(breakdown.active / (breakdown.active + breakdown.inactive)) * 100}%` 
              }}
            />
          </div>
        </div>
      )}

      {/* Trend */}
      {trend && (
        <div className="flex items-center text-sm">
          <span className="mr-1">{getTrendIcon()}</span>
          <span style={{ color: getTrendColor() }} className="font-medium">
            {Math.abs(trend.value).toFixed(1)}%
          </span>
          <span className="text-gray-500 ml-1">vs {trend.period}</span>
        </div>
      )}
    </div>
  );
};

export default DashboardMetricCard;
